import React from "react";
import { Link } from "react-router-dom";
import { Heart, Mail, Instagram, Facebook } from "lucide-react";
import NewsletterForm from "./NewsletterForm";
import { Editable } from "./Editable";
import { useContentApi } from "../context/ContentContext";
import { mailbox } from "../lib/mailboxes";

const Footer = () => {
    const { c } = useContentApi();
    const salesEmail = mailbox(c, "sales");
    return (
        <footer className="bg-[#1C252E] text-white/80 mt-24" data-testid="site-footer">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-12">
                    <div className="md:col-span-2">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-full bg-[#E53935] flex items-center justify-center">
                                <Heart className="w-5 h-5 text-white" fill="white" strokeWidth={1.5} />
                            </div>
                            <h3 className="font-heading text-2xl text-white font-medium">
                                Reddog Foundation
                            </h3>
                        </div>
                        <Editable
                            contentKey="footer.about"
                            as="p"
                            multiline
                            className="text-sm leading-relaxed max-w-md text-white/70"
                        >
                            Reddog Community Foundation Inc. supports schools, families, and community needs through restricted fundraising, responsible oversight, and public accountability. An independent nonprofit corporation.
                        </Editable>
                        <div className="mt-6">
                            <Editable contentKey="footer.tagline" as="p" className="text-xs uppercase tracking-[0.18em] text-[#E53935] font-bold mb-3">
                                Stay in the loop
                            </Editable>
                            <NewsletterForm source="footer" />
                        </div>
                        <div className="flex items-center gap-4 mt-8">
                            <a
                                href="https://instagram.com"
                                target="_blank"
                                rel="noreferrer"
                                className="p-2 rounded-full bg-white/5 hover:bg-[#E53935] transition-colors"
                                data-testid="footer-instagram-link"
                                aria-label="Instagram"
                            >
                                <Instagram className="w-4 h-4" strokeWidth={1.5} />
                            </a>
                            <a
                                href="https://facebook.com"
                                target="_blank"
                                rel="noreferrer"
                                className="p-2 rounded-full bg-white/5 hover:bg-[#E53935] transition-colors"
                                data-testid="footer-facebook-link"
                                aria-label="Facebook"
                            >
                                <Facebook className="w-4 h-4" strokeWidth={1.5} />
                            </a>
                            <a
                                href={`mailto:${salesEmail}`}
                                className="p-2 rounded-full bg-white/5 hover:bg-[#E53935] transition-colors"
                                data-testid="footer-email-link"
                                aria-label="Email"
                            >
                                <Mail className="w-4 h-4" strokeWidth={1.5} />
                            </a>
                        </div>
                        <address
                            data-testid="footer-mailing-address"
                            className="not-italic mt-8 text-xs text-white/60 leading-relaxed"
                        >
                            <p className="text-xs uppercase tracking-[0.18em] text-[#E53935] font-bold mb-2">Send a check</p>
                            <div>Reddog Community Foundation Inc.</div>
                            <div>PO Box 231</div>
                            <div>Frenchtown, MT 59834</div>
                            <a
                                href="tel:+14068020279"
                                data-testid="footer-phone-link"
                                className="inline-block mt-2 text-white/80 hover:text-white transition-colors"
                            >
                                (406) 802-0279
                            </a>
                        </address>
                    </div>

                    <div>
                        <Editable contentKey="footer.explore_title" as="p" className="text-xs uppercase tracking-[0.18em] text-[#E53935] font-bold mb-5">
                            Explore
                        </Editable>
                        <ul className="space-y-3 text-sm">
                            <li><Link to="/shop" className="hover:text-white transition-colors">Shop</Link></li>
                            <li><Link to="/donate" className="hover:text-white transition-colors">Donate</Link></li>
                            <li><Link to="/polls" className="hover:text-white transition-colors">Community polls</Link></li>
                            <li><Link to="/raffles" className="hover:text-white transition-colors">Raffles</Link></li>
                            <li><Link to="/services" className="hover:text-white transition-colors">Free community board</Link></li>
                            <li><Link to="/about" className="hover:text-white transition-colors">Our Mission</Link></li>
                            <li><Link to="/contact" data-testid="footer-contact-link" className="hover:text-white transition-colors">Contact us</Link></li>
                            <li><Link to="/cart" className="hover:text-white transition-colors">Cart</Link></li>
                        </ul>
                    </div>

                    <div>
                        <Editable contentKey="footer.apply_title" as="p" className="text-xs uppercase tracking-[0.18em] text-[#E53935] font-bold mb-5">
                            Get involved
                        </Editable>
                        <ul className="space-y-3 text-sm">
                            <li><Link to="/apply/contractor" data-testid="footer-apply-contractor-link" className="hover:text-white transition-colors">Apply as contractor</Link></li>
                            <li><Link to="/apply/grant" data-testid="footer-apply-grant-link" className="hover:text-white transition-colors">Apply for a micro-grant</Link></li>
                            <li><Link to="/about#volunteer" data-testid="footer-volunteer-link" className="hover:text-white transition-colors">Volunteer with us</Link></li>
                            <li><Link to="/donate" className="hover:text-white transition-colors">Sponsor a fund</Link></li>
                        </ul>
                    </div>

                    <div>
                        <Editable contentKey="footer.foundation_title" as="p" className="text-xs uppercase tracking-[0.18em] text-[#E53935] font-bold mb-5">
                            Governance
                        </Editable>
                        <ul className="space-y-3 text-sm">
                            <li><Link to="/financials" data-testid="footer-financials-link" className="hover:text-white transition-colors">Financial transparency</Link></li>
                            <li><Link to="/about#tax-status" data-testid="footer-disclosure-tax-status" className="hover:text-white transition-colors">Tax status</Link></li>
                            <li><Link to="/about#independent-organization" data-testid="footer-disclosure-independent" className="hover:text-white transition-colors">Independent organization</Link></li>
                            <li><Link to="/about#conflict-of-interest" data-testid="footer-disclosure-conflict" className="hover:text-white transition-colors">Conflict of interest</Link></li>
                            <li><Link to="/about#responsibility-disclaimer" data-testid="footer-disclosure-responsibility" className="hover:text-white transition-colors">Responsibility &amp; hold-harmless</Link></li>
                            <li><Link to="/about#ledger-privacy" data-testid="footer-disclosure-ledger-privacy" className="hover:text-white transition-colors">Ledger privacy</Link></li>
                            <li><Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white transition-colors">Privacy policy</Link></li>
                            <li><Link to="/legal/changes" data-testid="footer-legal-changes-link" className="hover:text-white transition-colors">Legal document changes</Link></li>
                        </ul>
                    </div>
                </div>

                <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/50">
                    <Editable contentKey="footer.copyright" as="p">
                        © Reddog Community Foundation Inc. All rights reserved.
                    </Editable>
                    <Link to="/about#governance" data-testid="footer-full-disclosures-link" className="hover:text-white transition-colors underline-offset-4 hover:underline">
                        Full governance &amp; disclosures →
                    </Link>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
